import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Film, ScanLine, Shield } from 'lucide-react';
import { creatorMediaUrl } from '../../lib/creatorApi';
import { clamp, fadeAt, findClip, trackAudible, updateClip } from '../../lib/creatorTimeline';
import { animationClock, clipAnimationTime, evaluateTransform, setKeyframe } from '../../lib/creatorAnimation';
import { visualSize } from '../../lib/creatorCanvas';
import CreatorTransformOverlay from './CreatorTransformOverlay';

const fontFamilies = { sans: 'Arial, "DejaVu Sans", sans-serif', serif: '"Times New Roman", "DejaVu Serif", serif', mono: '"Courier New", "DejaVu Sans Mono", monospace' };

export function transitionEnvelopes(project) {
  const envelopes = new Map();
  const get = id => {
    if (!envelopes.has(id)) envelopes.set(id, { visualIn: 0, visualOut: 0, audioIn: 0, audioOut: 0 });
    return envelopes.get(id);
  };
  project.tracks.forEach(track => track.clips.forEach(clip => {
    const transition = clip.transition_in;
    if (!transition || transition.type === 'cut' || !(transition.duration > 0)) return;
    if (!track.clips.some(previous => previous.id === transition.from_clip_id)) return;
    get(clip.id).visualIn = transition.duration;
    get(clip.id).audioIn = transition.duration;
    get(transition.from_clip_id).audioOut = transition.duration;
    if (transition.type === 'fade') get(transition.from_clip_id).visualOut = transition.duration;
  }));
  return envelopes;
}

export function transitionFadeAt(clip, time, envelopes, kind = 'visual') {
  const envelope = envelopes.get(clip.id);
  const local = time - clip.start;
  if (!envelope || local < 0 || local >= clip.duration) return fadeAt(clip, time);
  const incoming = envelope[`${kind}In`];
  const outgoing = envelope[`${kind}Out`];
  return fadeAt(clip, time) * Math.min(1, incoming > 0 ? local / incoming : 1,
    outgoing > 0 ? (clip.duration - local) / outgoing : 1);
}

function orderedClips(track) {
  return track.clips.some(clip => clip.transition_in)
    ? [...track.clips].sort((left, right) => left.start - right.start) : track.clips;
}

function MediaLayer({ asset, clip, track, project, time, playing, session, graph, onError, scale, envelopes, background = false, transformOverride }) {
  const element = useRef(null);
  const nodes = useRef(null);
  const [useFallback, setUseFallback] = useState(false);
  const url = creatorMediaUrl(asset.preview_mode === 'direct' && !useFallback ? asset.url : asset.proxy_url || asset.url, session);
  const active = time >= clip.start && time < clip.start + clip.duration;
  const visual = asset.kind !== 'audio' && track.type !== 'audio';
  const audible = !background && active && !clip.muted && trackAudible(project, track) && (asset.kind === 'audio' || asset.has_audio);
  const gain = audible ? Math.pow(10, (clip.volume_db + track.volume_db + project.settings.master_volume_db) / 20)
    * transitionFadeAt(clip, time, envelopes, 'audio') : 0;

  useEffect(() => {
    const media = element.current;
    if (!media || asset.kind === 'image') return;
    const synchronize = () => {
      const target = clamp((time - clip.start) * clip.speed + clip.source_start, 0, Math.max(0, asset.duration - 0.001));
      try {
        if (active && Math.abs(media.currentTime - target) > (playing ? 0.08 : 0.008)) media.currentTime = target;
        media.playbackRate = clip.speed;
        media.preservesPitch = true;
      } catch { /* Metadata may not have arrived yet. loadedmetadata retries. */ }
    };
    synchronize();
    media.addEventListener('loadedmetadata', synchronize);
    return () => media.removeEventListener('loadedmetadata', synchronize);
  }, [time, active, playing, clip.start, clip.source_start, clip.speed, asset.duration, asset.kind]);

  useEffect(() => {
    const media = element.current;
    if (!media || asset.kind === 'image') return;
    if (playing && active) {
      try {
        if (!background && (asset.has_audio || asset.kind === 'audio')) nodes.current = graph(media);
        media.play()?.catch(error => {
          if (error.name !== 'AbortError') onError(`${asset.filename}: no se pudo reproducir. Revisa el formato o vuelve a pulsar reproducir.`);
        });
      } catch { onError(`${asset.filename}: no se pudo iniciar el audio de la vista previa.`); }
    } else media.pause();
  }, [playing, active, graph, onError, asset.filename, asset.has_audio, asset.kind, background]);

  useEffect(() => {
    const media = element.current;
    if (!media || asset.kind === 'image') return;
    if (background) { media.muted = true; media.volume = 0; }
    else if (nodes.current) {
      nodes.current.gain.gain.setValueAtTime(gain, nodes.current.context.currentTime);
      if (nodes.current.pan) nodes.current.pan.pan.setValueAtTime(clip.pan, nodes.current.context.currentTime);
      media.volume = 1;
      media.muted = false;
    } else { media.volume = clamp(gain, 0, 1); media.muted = !audible; }
  }, [gain, audible, clip.pan, playing, asset.kind, background]);

  useEffect(() => {
    const media = element.current;
    return () => {
      if (media && asset.kind !== 'image') media.pause();
      nodes.current?.source.disconnect();
      nodes.current?.gain.disconnect();
      nodes.current?.pan?.disconnect();
    };
  }, [asset.kind]);

  const transform = transformOverride || evaluateTransform(clip, time);
  const blur = project.canvas.blur_strength ?? 20;
  // Overscan three Gaussian sigmas on each edge in preview and export.
  const factor = background
    ? Math.max((project.canvas.width + 6 * blur) / asset.width, (project.canvas.height + 6 * blur) / asset.height)
    : transform.fit === 'fill'
    ? Math.max(project.canvas.width / (asset.width || project.canvas.width), project.canvas.height / (asset.height || project.canvas.height))
    : Math.min(project.canvas.width / (asset.width || project.canvas.width), project.canvas.height / (asset.height || project.canvas.height));
  const style = visual ? {
    display: active && !track.hidden ? 'block' : 'none',
    width: `${(asset.width || project.canvas.width) * factor * scale}px`,
    height: `${(asset.height || project.canvas.height) * factor * scale}px`,
    left: `${(background ? 0.5 : transform.x) * 100}%`, top: `${(background ? 0.5 : transform.y) * 100}%`,
    transform: background ? 'translate(-50%, -50%)' : `translate(-50%, -50%) rotate(${transform.rotation}deg) scale(${transform.scale})`,
    opacity: transform.opacity * track.opacity * transitionFadeAt(clip, time, envelopes),
    filter: `url(#creator-color-${clip.id}${background ? '-background' : ''})${background ? ` blur(${blur * scale}px) brightness(${1 - (project.canvas.blur_darken ?? 0.35)})` : ''}`,
  } : { display: 'none' };
  const contrast = clip.color.contrast;
  const intercept = clip.color.brightness + (1 - contrast) / 2;
  const Media = asset.kind === 'image' ? 'img' : asset.kind === 'audio' || track.type === 'audio' ? 'audio' : 'video';
  return <>
    {visual && <svg width="0" height="0" aria-hidden="true" className="creator-filter"><defs><filter id={`creator-color-${clip.id}${background ? '-background' : ''}`} colorInterpolationFilters="sRGB">
      <feComponentTransfer><feFuncR type="linear" slope={contrast} intercept={intercept} /><feFuncG type="linear" slope={contrast} intercept={intercept} /><feFuncB type="linear" slope={contrast} intercept={intercept} /></feComponentTransfer>
      <feColorMatrix type="saturate" values={clip.color.saturation} />
    </filter></defs></svg>}
    <Media ref={element} className="creator-media" src={url || undefined} style={style} data-clip-id={clip.id} data-background={background || undefined}
      {...(asset.kind === 'image' ? { alt: asset.filename, draggable: false } : { playsInline: true, preload: 'auto', crossOrigin: 'anonymous', muted: background,
        'aria-label': background ? undefined : asset.filename, 'aria-hidden': background || undefined })}
      referrerPolicy="no-referrer" onError={() => {
        if (asset.preview_mode === 'direct' && !useFallback && asset.proxy_url) setUseFallback(true);
        else onError(`${asset.filename}: vista previa no disponible. Revisa el archivo o su formato.`);
      }} />
  </>;
}

export default function CreatorPreview({ project, session, time, playing, onError, selected = [], onSelect, onChange, onInteract }) {
  const stage = useRef(null);
  const well = useRef(null);
  const context = useRef(null);
  const sources = useRef(new WeakMap());
  const closeTimer = useRef(null);
  const [width, setWidth] = useState(280);
  const [previewMode, setPreviewMode] = useState('fit');
  const [safeGuides, setSafeGuides] = useState(false);
  const [draftTransform, setDraftTransform] = useState(null);
  const [textSize, setTextSize] = useState(null);
  const selectedId = selected[0];
  const chosen = findClip(project, selectedId);
  const displayWidth = previewMode === 'fit' ? width : project.canvas.width * Number(previewMode);
  useEffect(() => setDraftTransform(null), [selectedId]);
  useLayoutEffect(() => {
    const node = stage.current?.querySelector(`[data-preview-text="${selectedId}"]`);
    setTextSize(previous => {
      const next = node ? { width: node.offsetWidth, height: node.offsetHeight } : null;
      return previous?.width === next?.width && previous?.height === next?.height ? previous : next;
    });
  }, [selectedId, chosen?.clip.text?.content, chosen?.clip.text?.size, chosen?.clip.text?.font, chosen?.clip.text?.bold, displayWidth]);
  const envelopes = useMemo(() => transitionEnvelopes({ tracks: project.tracks }), [project.tracks]);
  useEffect(() => {
    const measure = () => {
      const node = well.current;
      if (!node?.clientWidth || !node.clientHeight) return;
      const style = getComputedStyle(node);
      const horizontal = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      const vertical = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      setWidth(Math.max(1, Math.min(node.clientWidth - horizontal, (node.clientHeight - vertical) * project.canvas.width / project.canvas.height)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    if (well.current) observer.observe(well.current);
    return () => observer.disconnect();
  }, [project.canvas.width, project.canvas.height]);
  const graph = useCallback(media => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return null;
    if (!context.current) context.current = new AudioContext();
    const audio = context.current;
    audio.resume().catch(() => onError('El navegador suspendio el audio. Vuelve a pulsar reproducir.'));
    const existing = sources.current.get(media);
    if (existing) {
      existing.source.connect(existing.gain);
      existing.gain.connect(existing.pan || audio.destination);
      existing.pan?.connect(audio.destination);
      return existing;
    }
    const source = audio.createMediaElementSource(media);
    const gain = audio.createGain();
    gain.channelCount = 2;
    gain.channelCountMode = 'explicit';
    gain.channelInterpretation = 'speakers';
    const pan = audio.createStereoPanner ? audio.createStereoPanner() : null;
    source.connect(gain);
    gain.connect(pan || audio.destination);
    pan?.connect(audio.destination);
    const nodes = { source, gain, pan, context: audio };
    sources.current.set(media, nodes);
    return nodes;
  }, [onError]);
  useEffect(() => {
    clearTimeout(closeTimer.current);
    return () => { closeTimer.current = setTimeout(() => { context.current?.close().catch(() => {}); context.current = null; }, 0); };
  }, []);
  const scale = displayWidth / project.canvas.width;
  const commitTransform = (transform, atTime) => {
    if (!chosen || chosen.track.locked) return;
    onChange?.(value => updateClip(value, chosen.clip.id, clip => {
      let next = { ...clip, transform: { ...clip.transform } };
      const before = evaluateTransform(clip, atTime);
      for (const property of ['x', 'y', 'scale', 'rotation', 'opacity']) {
        if (Math.abs(transform[property] - before[property]) < 1e-6) continue;
        if (clip.keyframes?.some(frame => frame.property === property)) {
          const stamp = animationClock(clip, clipAnimationTime(clip, atTime));
          const interpolation = clip.keyframes.find(frame => frame.property === property && Math.abs(frame.time - stamp) < 1e-6)?.interpolation || 'linear';
          next = setKeyframe(next, property, atTime, transform[property], interpolation);
        } else next.transform[property] = transform[property];
      }
      return next;
    }), 'Transformar capa');
  };
  const chooseAt = event => {
    if (event.button !== 0 || !onSelect) return;
    const rect = stage.current.getBoundingClientRect();
    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    for (const track of [...project.tracks].reverse()) {
      if (track.hidden || track.type === 'audio') continue;
      for (const clip of [...orderedClips(track)].reverse()) {
        if (time < clip.start || time >= clip.start + clip.duration) continue;
        const asset = project.assets.find(item => item.id === clip.asset_id);
        if (!clip.text && asset?.kind === 'audio') continue;
        const transform = evaluateTransform(clip, time);
        const node = clip.text && stage.current.querySelector(`[data-preview-text="${clip.id}"]`);
        const size = visualSize(asset, clip, project.canvas, scale, node ? { width: node.offsetWidth, height: node.offsetHeight } : null);
        const dx = point.x - transform.x * displayWidth, dy = point.y - transform.y * displayWidth * project.canvas.height / project.canvas.width;
        const angle = -transform.rotation * Math.PI / 180;
        if (Math.abs(dx * Math.cos(angle) - dy * Math.sin(angle)) <= size.width * transform.scale / 2
          && Math.abs(dx * Math.sin(angle) + dy * Math.cos(angle)) <= size.height * transform.scale / 2) {
          onInteract?.(); onSelect([clip.id]); return;
        }
      }
    }
    onSelect([]);
  };
  const selectedTransform = chosen && (draftTransform || evaluateTransform(chosen.clip, time));
  return <div className={`creator-preview-well ${previewMode !== 'fit' ? 'is-zoomed' : ''}`} ref={well}>
    <div className="creator-preview-controls"><button type="button" className="creator-icon" aria-label="Ajustar vista previa" title="Ajustar vista previa" onClick={() => setPreviewMode('fit')}><ScanLine /></button>
      <select aria-label="Zoom de vista previa" value={previewMode} onChange={event => setPreviewMode(event.target.value)}><option value="fit">Ajustar</option><option value="0.5">50%</option><option value="1">100%</option><option value="2">200%</option></select>
      <button type="button" className={`creator-icon ${safeGuides ? 'is-active' : ''}`} aria-label="Zonas seguras" title="Zonas seguras" aria-pressed={safeGuides} onClick={() => setSafeGuides(value => !value)}><Shield /></button></div>
    <div ref={stage} className="creator-stage" data-testid="creator-preview" role="img" aria-label={`Vista previa ${project.canvas.width} por ${project.canvas.height}`}
      onPointerDown={chooseAt}
      style={{ aspectRatio: `${project.canvas.width} / ${project.canvas.height}`, background: project.canvas.background,
        width: `${displayWidth}px`, flexShrink: 0 }}>
      {!project.tracks.some(track => track.clips.length) && <Film className="creator-empty-film" aria-hidden="true" />}
      {project.canvas.background_mode === 'blur_video' && project.tracks.filter(track => !track.hidden && track.type !== 'audio').map(track =>
        orderedClips(track).map(clip => {
          const asset = project.assets.find(item => item.id === clip.asset_id);
          return asset?.kind === 'video' ? <MediaLayer key={`background-${clip.id}`} background {...{ asset, clip, track, project, time, playing, session, graph, onError, scale, envelopes }} transformOverride={clip.id === selectedId ? draftTransform : null} /> : null;
        }))}
      {project.tracks.map(track => <React.Fragment key={track.id}>
        {orderedClips(track).map(clip => {
          const asset = project.assets.find(item => item.id === clip.asset_id);
          if (asset) return <MediaLayer key={clip.id} {...{ asset, clip, track, project, time, playing, session, graph, onError, scale, envelopes }} transformOverride={clip.id === selectedId ? draftTransform : null} />;
          if (!clip.text || track.hidden || time < clip.start || time >= clip.start + clip.duration) return null;
          const transform = clip.id === selectedId && draftTransform ? draftTransform : evaluateTransform(clip, time);
          return <div key={clip.id} className="creator-preview-text" data-preview-text={clip.id} style={{ left: `${transform.x * 100}%`, top: `${transform.y * 100}%`,
            transform: `translate(-50%, -50%) rotate(${transform.rotation}deg) scale(${transform.scale})`,
            opacity: transform.opacity * track.opacity * transitionFadeAt(clip, time, envelopes), fontFamily: fontFamilies[clip.text.font],
            fontSize: `${clip.text.size * scale}px`, color: clip.text.color, background: clip.text.background,
            fontWeight: clip.text.bold ? 700 : 400, textAlign: clip.text.align }}>{clip.text.content}</div>;
        })}
      </React.Fragment>)}
      {safeGuides && <div className="creator-safe-guides" aria-hidden="true" />}
      {chosen && chosen.track.type !== 'audio' && !chosen.track.hidden && time >= chosen.clip.start && time < chosen.clip.start + chosen.clip.duration && onChange && <CreatorTransformOverlay
        transform={selectedTransform} width={displayWidth} height={displayWidth * project.canvas.height / project.canvas.width}
        size={visualSize(chosen.asset, chosen.clip, project.canvas, scale, textSize)} locked={chosen.track.locked} time={time}
        onDraft={setDraftTransform} onCommit={commitTransform} onStart={onInteract} />}
    </div>
  </div>;
}
